/**
 * SOVRANOVA — Meditation Hub
 * Mindfulness meditation surface. Four ways to practise: Guided · Frequency-Enhanced
 * · Evening Wind-Down · Silent. Each session is AI-personalised from memory via the
 * MeditationAgent and played through the shared GuidedHypnosisPlayer (pointed at the
 * /meditation API). Flow: mode → theme → length → player.
 * Gated by VITE_FEATURE_MEDITATION (frontend) + FEATURE_MEDITATION (backend).
 */
import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import { GuidedHypnosisPlayer } from '../hypnosis/GuidedHypnosisPlayer'

// Yoga Nidra has a FLOOR, unlike every other practice here. The rotation of
// consciousness alone is 64 points in the short form and 100 in the full one; at five
// or ten minutes the rotation would consume the entire session and the other seven
// stages would vanish. Twenty is the shortest length at which it is still the practice.
// The backend clamps this too — a UI-only floor is exactly the class of drift that has
// bitten this codebase before (see the session-length whitelist, d6fc6e1).
const NIDRA_MIN_MINUTES = 20

const TOPICS = [
  { id: 'breath',          emoji: '🫁', name: 'Breath Awareness',   tag: 'Anchor · Calm · Focus' },
  { id: 'body_scan',       emoji: '🧘', name: 'Body Scan',          tag: 'Release · Presence · Rest' },
  { id: 'present_moment',  emoji: '🌿', name: 'Present Moment',      tag: 'Mindfulness · Ground' },
  { id: 'loving_kindness', emoji: '💗', name: 'Loving-Kindness',     tag: 'Warmth · Compassion' },
  { id: 'gratitude',       emoji: '🙏', name: 'Gratitude',          tag: 'Appreciation · Ease' },
  { id: 'stress_release',  emoji: '🌊', name: 'Stress Release',      tag: 'Unwind · Soften · Settle' },
  { id: 'open_awareness',  emoji: '🌌', name: 'Open Awareness',      tag: 'Spacious · Still · Clear' },
  { id: 'wind_down',       emoji: '🌙', name: 'Evening Wind-Down',   tag: 'Rest · Slow · Let go' },
]

const LENGTHS = [
  { min: 5,  emoji: '⏳', name: '5 minutes',  tag: 'A short reset' },
  { min: 10, emoji: '⏳', name: '10 minutes', tag: 'A deeper settle' },
  { min: 15, emoji: '⏳', name: '15 minutes', tag: 'A full practice' },
  { min: 20, emoji: '⏳', name: '20 minutes', tag: 'A long, spacious sit' },
]

// Nidra gets its own list — see NIDRA_MIN_MINUTES. 30 runs the full rotation of
// consciousness; 20 runs the abbreviated form of the same sequence.
const NIDRA_LENGTHS = [
  // Honest labels, from David's timed listens (20 Aug 2026): the shorter
  // practice runs ~25 min, the full one ~40. The `min` values stay 20/30 —
  // they are the GENERATION targets the backend keys on, not durations.
  { min: 20, emoji: '⏳', name: 'About 25 minutes', tag: 'The practice, in its shorter form' },
  { min: 30, emoji: '⏳', name: 'About 40 minutes', tag: 'The full rotation, unhurried' },
]

// NOTE: mode ids match the shared player's engine (guided/frequency/sleep/subconscious),
// so the same gapless-audio playback works unchanged — only the labels differ here.
// Yoga Nidra is appended only when the SERVER says it is on — there is deliberately no
// VITE_FEATURE_NIDRA. A client flag and a server flag that must agree is a split brain:
// with the client on and the server off, a person would pick Yoga Nidra and silently be
// given a standard guided meditation under that name. One switch, on the server, and the
// card cannot appear for a practice the backend will not actually produce.
const NIDRA_MODE = {
  id: 'nidra',
  emoji: '🪷',
  name: 'Yoga Nidra',
  desc: 'A long, structured practice: you lie down, set a resolve in your own words, and are guided in a fixed sequence through the whole body, the breath, and a few simple images. The aim is to stay just awake at the edge of sleep — not to fall asleep.',
  duration: 'about 25 or 40 min · lie down',
  color: '#a06fb5',
  lieDown: true,
}

// ── The Nidra Guide ──────────────────────────────────────────────────────────
// Yoga Nidra is the one practice here with a fixed, named shape, and most people
// arrive not knowing it. Dedicated nidra apps put a "Guide" beside the session for
// exactly this reason, and it belongs there — next to the choice, not inside the
// player, where it would be one more thing to read while lying down. Optional and
// collapsed by default. Descriptive only: what happens, never what it will do for
// you — an outcome promise here would be a claim the session then has to keep.
const NIDRA_GUIDE_STAGES = [
  ['Settling',                  'You lie down, still and warm, and are guided to arrive.'],
  ['A resolve',                 'A short phrase of your own, said inwardly three times. No one hears it but you.'],
  ['Rotation of consciousness', 'Attention is guided through the body, one part at a time, in a set order. Nothing to do — just listen and follow.'],
  ['Breath awareness',          'Counting breaths backward, softly.'],
  ['Opposite sensations',       'Heaviness then lightness, or warmth then coolness — each felt fully.'],
  ['Simple images',             'A few images offered lightly, then let go.'],
  ['The resolve again',         'The same phrase, in the same words.'],
  ['Returning',                 'A gentle, unhurried return to the room — or, in the sleep-leaning practices, permission to drift.'],
]

function NidraGuide({ open, onToggle }) {
  if (!open) {
    return (
      <button style={s.guideToggle} onClick={onToggle}>&#9432; What happens in a session</button>
    )
  }
  return (
    <div style={s.guideCard}>
      <p style={s.guideTitle}>What happens in a session</p>
      <p style={s.guideLead}>
        Yoga Nidra follows the same arc every time — the sameness is part of the practice.
        Nothing is asked of you except to lie still and listen.
      </p>
      <ol style={s.guideList}>
        {NIDRA_GUIDE_STAGES.map(([name, desc]) => (
          <li key={name} style={s.guideStep}>
            <span style={s.guideStepName}>{name}.</span> {desc}
          </li>
        ))}
      </ol>
      <p style={s.guideNote}>
        Lie on your back somewhere warm — a blanket helps, because a body this still cools
        down. No headphones needed. The aim is to stay just awake, listening at the edge
        of sleep.
      </p>
      <button style={s.guideClose} onClick={onToggle}>Close</button>
    </div>
  )
}

// Intent names, tags and ORDER all come from the server, because the intent id decides
// which body sequence gets spliced into the script. Only the emoji lives here, because
// it is decoration and nothing downstream depends on it.
const NIDRA_INTENT_EMOJI = {
  nidra_rest:    '🪷',
  nidra_sleep:   '🌙',
  nidra_anxiety: '🌀',
  nidra_gentle:  '🕊️',
  nidra_restore: '☀️',
}

const MODES = [
  {
    id: 'guided',
    emoji: '🕯️',
    name: 'Guided Meditation',
    desc: 'A warm voice guides your attention — breath, body, or a simple focus — with plenty of silence between cues. Eyes closed. No headphones needed.',
    duration: 'Choose length',
    color: '#4c9c8b',
  },
  {
    id: 'frequency',
    emoji: '🔊',
    name: 'Frequency-Enhanced',
    desc: 'The same guided practice with a soft background tone underneath to help you settle. Headphones recommended.',
    duration: 'Choose length',
    color: '#c9a84c',
    headphones: true,
  },
  {
    id: 'sleep',
    emoji: '🌙',
    name: 'Evening Wind-Down',
    desc: 'A slow, gentle practice that releases the day and trails into stillness — no re-alerting — so you can set your device down and drift toward sleep.',
    duration: 'Voice + soft tone',
    color: '#4a7a9b',
  },
  {
    id: 'subconscious',
    emoji: '🌫️',
    name: 'Silent',
    desc: 'No voice — a soft tone with a few gentle intentions that rest on screen. Best used awake and resting, letting your eyes settle on each phrase.',
    duration: 'Tone + intentions',
    color: '#8b9c6f',
    headphones: true,
  },
]

export function MeditationHub({ onClose, onHome }) {
  const [step,    setStep]    = useState('mode')
  const [mode,    setMode]    = useState(null)
  const [topic,   setTopic]   = useState(null)
  const [minutes, setMinutes] = useState(5)
  const [safetyOpen, setSafetyOpen] = useState(() => {
    try { return localStorage.getItem('sov_med_safety_v1') !== '1' } catch { return true }
  })
  const dismissSafety = () => { setSafetyOpen(false); try { localStorage.setItem('sov_med_safety_v1', '1') } catch {} }
  // Collapsed by default, and deliberately NOT persisted like the safety card: the
  // guide is a reference, not an acknowledgement, and someone three weeks in may
  // want it again exactly as much as on day one.
  const [guideOpen, setGuideOpen] = useState(false)

  // Ask the server whether Yoga Nidra is on. Fails CLOSED: any error leaves the card
  // hidden, which is the correct direction for an unreleased practice.
  const [modes, setModes] = useState(MODES)
  // The Nidra intents come from the SERVER, for the same reason the flag does: the
  // intent id decides which body sequence gets spliced in, so a client-side list
  // could drift out of step with the sequences that actually exist. Empty here means
  // "this server has no intents" — an older backend, or a failed fetch — and the flow
  // falls back to exactly what shipped before intents existed.
  const [nidraIntents, setNidraIntents] = useState([])
  useEffect(() => {
    let cancelled = false
    api.get('/meditation/catalogue')
      .then(r => {
        if (cancelled || !r || !r.nidraEnabled) return
        setModes([...MODES, NIDRA_MODE])
        setNidraIntents(Object.entries(r.nidraIntents || {}).map(([id, v]) => ({
          id, name: v.name, tag: v.tag, emoji: NIDRA_INTENT_EMOJI[id] || '🪷',
        })))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  if (step === 'player') {
    return (
      <GuidedHypnosisPlayer
        apiBase="/meditation"
        topic={topic}
        mode={mode}
        minutes={minutes}
        onClose={() => setStep('length')}
        onHome={onHome || onClose}
        onComplete={() => { setStep('mode'); setMode(null); setTopic(null); setMinutes(5) }}
      />
    )
  }

  // Nidra with no intents on offer skipped the topic step on the way in, so it must
  // skip it on the way out too — otherwise Back lands on an empty chooser.
  const skippedTopicStep = mode === 'nidra' && nidraIntents.length === 0
  const goBack =
    step === 'length' ? () => setStep(skippedTopicStep ? 'mode' : 'topic')
    : step === 'topic' ? () => setStep('mode')
    : onClose

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={goBack}>&#8592; Back</button>
        <p style={s.headerTitle}>
          {step === 'mode' ? 'Meditation' : `${modes.find(m => m.id === mode)?.name || ''}`}
        </p>
        <button style={s.homeBtn} onClick={onHome || onClose} title="Dashboard">⌂ Home</button>
      </div>

      {step === 'mode' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>Choose your practice</h1>
            <p style={s.subtitle}>Every meditation is written for you — your name, your chosen theme and your preferences.</p>
          </div>

          {safetyOpen ? (
            <div style={s.safetyCard}>
              <p style={s.safetyTitle}>Before you begin</p>
              <ul style={s.safetyList}>
                <li style={s.safetyItem}>Settle somewhere you won't be disturbed — please don't practise while driving or operating machinery.</li>
                <li style={s.safetyItem}>You're always in control — pause, stop, or open your eyes anytime. If sitting quietly ever leaves you more restless or distressed than settled (it can, for some people, especially with significant trauma), gently ease off and come back another time.</li>
                <li style={s.safetyItem}>This is a wellness and mindfulness practice — not therapy or medical treatment, and not a substitute for professional care. If you're in crisis, use Urgent help or your local emergency services.</li>
              </ul>
              <button style={s.safetyAck} onClick={dismissSafety}>I understand</button>
            </div>
          ) : (
            <button style={s.safetyReopen} onClick={() => setSafetyOpen(true)}>&#9432; Safety &amp; how to practise</button>
          )}

          <div style={s.modeGrid}>
            {modes.map(m => (
              <button key={m.id} style={s.modeCard} onClick={() => {
                setMode(m.id)
                // Nidra never gets the meditation THEMES. The practice is not
                // "loving-kindness yoga nidra" or "gratitude yoga nidra". What it does
                // get is an INTENT, which is a different thing: it chooses which body
                // sequence and pace the session actually uses, not a subject matter.
                // If the server offers none (older backend, or the fetch failed) this
                // falls back to exactly the pre-intent flow: straight to length, with
                // body_scan as the catalogue entry because the rotation IS a body scan.
                if (m.id === 'nidra') {
                  setMinutes(NIDRA_MIN_MINUTES)
                  if (nidraIntents.length) { setTopic(null); setStep('topic') }
                  else { setTopic('body_scan'); setStep('length') }
                }
                else setStep('topic')
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={s.modeEmoji}>{m.emoji}</span>
                  <span style={s.modeDuration}>{m.duration}</span>
                </div>
                <h3 style={s.modeName}>{m.name}</h3>
                <p style={s.modeDesc}>{m.desc}</p>
                {m.headphones && (<p style={s.headphoneTag}>🎧 Headphones recommended</p>)}
                {m.lieDown && (<p style={s.headphoneTag}>🛏️ Lie down somewhere you can stay still</p>)}
                <div style={{ ...s.modeBar, background: m.color }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'topic' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>What do you need today?</h1>
            <p style={s.subtitle}>{mode === 'nidra'
              ? 'This chooses how the body sequence itself is guided — how much of it there is, and how fast or slow it moves. There is no wrong answer, and you can pick a different one next time.'
              : 'Your practice is written around the theme you choose.'}</p>
          </div>

          <div style={s.topicGrid}>
            {(mode === 'nidra' ? nidraIntents : TOPICS).map(t => (
              <button key={t.id} style={s.topicCard}
                onClick={() => { setTopic(t.id); setStep('length') }}>
                <span style={s.topicEmoji}>{t.emoji}</span>
                <div>
                  <p style={s.topicName}>{t.name}</p>
                  <p style={s.topicTag}>{t.tag}</p>
                </div>
              </button>
            ))}
          </div>

          {mode === 'nidra' && (
            <NidraGuide open={guideOpen} onToggle={() => setGuideOpen(o => !o)} />
          )}
        </div>
      )}

      {step === 'length' && (
        <div style={s.content}>
          <div style={s.intro}>
            <h1 style={s.title}>How long?</h1>
            <p style={s.subtitle}>{mode === 'nidra'
              ? 'Yoga Nidra needs room to unfold — these are the two lengths at which it is still the whole practice. Lie down, and you can stop any time.'
              : 'Choose a length. You can stop any time.'}</p>
          </div>

          <div style={s.topicGrid}>
            {(mode === 'nidra' ? NIDRA_LENGTHS : LENGTHS).map(l => (
              <button key={l.min} style={s.topicCard}
                onClick={() => { setMinutes(l.min); setStep('player') }}>
                <span style={s.topicEmoji}>{l.emoji}</span>
                <div>
                  <p style={s.topicName}>{l.name}</p>
                  <p style={s.topicTag}>{l.tag}</p>
                </div>
              </button>
            ))}
          </div>

          {/* The fallback flow (no intents from the server) skips the topic step, so
              this is the guide's only chance to be seen on that path. */}
          {skippedTopicStep && (
            <NidraGuide open={guideOpen} onToggle={() => setGuideOpen(o => !o)} />
          )}
        </div>
      )}
    </div>
  )
}

const s = {
  page:        { display:'flex', flexDirection:'column', height:'100%', background:'linear-gradient(170deg,#08201a,#05170f,#03110b)', overflowY:'auto' },
  header:      { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.25rem', borderBottom:'1px solid rgba(255,255,255,.07)', background:'rgba(5,20,15,.95)', flexShrink:0, position:'sticky', top:0, zIndex:10 },
  backBtn:     { background:'transparent', border:'none', color:'rgba(255,255,255,.45)', fontSize:'0.875rem', cursor:'pointer', width:60, textAlign:'left' },
  homeBtn:     { background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.65)', borderRadius:'0.5rem', padding:'0.3rem 0.65rem', fontSize:'0.78rem', cursor:'pointer', fontWeight:500 },
  headerTitle: { fontSize:'0.9rem', fontWeight:600, color:'#eaf5ee', letterSpacing:'0.06em' },
  content:     { flex:1, padding:'1.5rem 1.25rem 2rem', display:'flex', flexDirection:'column', gap:'1.5rem' },
  intro:       { textAlign:'center' },
  title:       { fontSize:'1.5rem', fontWeight:700, color:'#eaf5ee', marginBottom:'0.5rem' },
  subtitle:    { fontSize:'0.875rem', color:'rgba(234,245,238,.45)', lineHeight:1.6 },
  modeGrid:    { display:'flex', flexDirection:'column', gap:'0.875rem' },
  modeCard:    { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1.25rem', padding:'1.25rem', textAlign:'left', cursor:'pointer', position:'relative', overflow:'hidden', transition:'all .2s' },
  modeEmoji:   { fontSize:'1.6rem', display:'block', marginBottom:'0.5rem' },
  modeDuration:{ fontSize:'0.7rem', color:'rgba(255,255,255,.35)', letterSpacing:'0.08em', background:'rgba(255,255,255,.06)', padding:'0.2rem 0.5rem', borderRadius:'1rem' },
  modeName:    { fontSize:'1.05rem', fontWeight:700, color:'#eaf5ee', marginBottom:'0.4rem' },
  modeDesc:    { fontSize:'0.825rem', color:'rgba(234,245,238,.5)', lineHeight:1.6, marginBottom:'0.5rem' },
  headphoneTag:{ fontSize:'0.72rem', color:'#c9a84c', marginTop:'0.25rem' },
  modeBar:     { position:'absolute', bottom:0, left:0, right:0, height:3, opacity:0.6 },
  topicGrid:   { display:'flex', flexDirection:'column', gap:'0.625rem' },
  topicCard:   { display:'flex', alignItems:'center', gap:'1rem', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)', borderRadius:'1rem', padding:'0.875rem 1.125rem', textAlign:'left', cursor:'pointer', transition:'all .2s' },
  topicEmoji:  { fontSize:'1.5rem', flexShrink:0 },
  topicName:   { fontSize:'0.95rem', fontWeight:600, color:'#eaf5ee', marginBottom:'0.15rem' },
  topicTag:    { fontSize:'0.75rem', color:'rgba(234,245,238,.4)' },
  safetyCard:  { background:'rgba(255,255,255,.035)', border:'1px solid rgba(76,156,139,.3)', borderRadius:'1rem', padding:'1rem 1.1rem' },
  safetyTitle: { fontSize:'0.8rem', fontWeight:700, color:'#6fc9b0', letterSpacing:'0.04em', marginBottom:'0.5rem' },
  safetyList:  { listStyle:'disc', paddingLeft:'1.1rem', margin:0, display:'flex', flexDirection:'column', gap:'0.45rem' },
  safetyItem:  { fontSize:'0.78rem', color:'rgba(234,245,238,.6)', lineHeight:1.55 },
  safetyAck:   { marginTop:'0.85rem', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'#eaf5ee', borderRadius:'0.6rem', padding:'0.45rem 1rem', fontSize:'0.8rem', cursor:'pointer' },
  safetyReopen:{ alignSelf:'flex-start', background:'transparent', border:'none', color:'rgba(111,201,176,.7)', fontSize:'0.76rem', cursor:'pointer', padding:0 },
  // ── Nidra guide ── quiet reference styling, sibling to the safety card but with
  // the nidra mode's own lotus purple so it reads as part of that practice.
  guideToggle: { alignSelf:'center', background:'transparent', border:'none', color:'rgba(160,111,181,.85)', fontSize:'0.8rem', cursor:'pointer', padding:'0.25rem 0' },
  guideCard:   { background:'rgba(255,255,255,.035)', border:'1px solid rgba(160,111,181,.35)', borderRadius:'1rem', padding:'1rem 1.1rem' },
  guideTitle:  { fontSize:'0.8rem', fontWeight:700, color:'#c9a3d9', letterSpacing:'0.04em', marginBottom:'0.5rem' },
  guideLead:   { fontSize:'0.78rem', color:'rgba(234,245,238,.6)', lineHeight:1.6, marginBottom:'0.75rem' },
  guideList:   { listStyle:'decimal', paddingLeft:'1.2rem', margin:0, display:'flex', flexDirection:'column', gap:'0.45rem' },
  guideStep:   { fontSize:'0.78rem', color:'rgba(234,245,238,.6)', lineHeight:1.55 },
  guideStepName:{ color:'rgba(234,245,238,.85)', fontWeight:600 },
  guideNote:   { fontSize:'0.78rem', color:'rgba(234,245,238,.6)', lineHeight:1.6, borderLeft:'2px solid rgba(160,111,181,.35)', paddingLeft:'0.9rem', marginTop:'0.85rem' },
  guideClose:  { marginTop:'0.85rem', background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', color:'#eaf5ee', borderRadius:'0.6rem', padding:'0.45rem 1rem', fontSize:'0.8rem', cursor:'pointer' },
}
